import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { Tag } from 'primereact/tag';
import { useGetSuggestions } from '../../queries/marketing';

const DISMISS_KEY = 'crm_dismissed_suggestions';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const SEVERITY_TAG = { action: 'danger', warning: 'warning', tip: 'info', info: 'secondary' };
const SEVERITY_ICON = {
  action:  'pi pi-bolt',
  warning: 'pi pi-exclamation-triangle',
  tip:     'pi pi-lightbulb',
  info:    'pi pi-info-circle',
};

// MySQL DAYOFWEEK: 1=Sunday, 2=Monday, ..., 7=Saturday.
const DOW_KEYS = ['', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const formatHour = (h) => {
  try {
    return new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return `${h}:00`;
  }
};

// Trigger-type → automation key mapping (mirrors backend keyMap).
const TRIGGER_KEY = {
  birthday_tomorrow: 'birthday',
  inactive_14days:   'winback',
  spin_winner:       'spin',
};

function getDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); }
  catch { return {}; }
}

function saveDismissed(map) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(map)); } catch { /* quota full */ }
}

function SuggestionCta({ cta, t, onViewChange }) {
  if (!cta || cta.kind === 'none') return null;

  const handlers = {
    automations: () => onViewChange?.('automations'),
    quota:       () => onViewChange?.('campaigns'),
    customers:   () => { window.location.href = '/customers'; },
    campaign:    () => onViewChange?.('campaigns'),
  };

  const labelKeys = {
    automations: 'marketing.suggest_cta_automations',
    quota:       'marketing.suggest_cta_quota',
    customers:   'marketing.suggest_cta_customers',
    campaign:    'marketing.suggest_cta_campaign',
  };

  const handler = handlers[cta.kind];
  const labelKey = labelKeys[cta.kind];
  if (!handler || !labelKey) return null;

  return (
    <div className="mt-1">
      <Button label={t(labelKey)} size="small" outlined onClick={handler} />
    </div>
  );
}

/**
 * Smart CRM Step 10 — rule-based suggestions panel.
 * Each suggestion is grounded in this venue's own data (no generic tips).
 * Dismissed suggestions are persisted to localStorage (no backend writes).
 */
export default function SuggestionsPanel({ onViewChange }) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetSuggestions();
  const suggestions = data?.suggestions ?? [];

  const [dismissed, setDismissedState] = useState(getDismissed);

  const dismiss = (code) => {
    const updated = { ...dismissed, [code]: Date.now() };
    setDismissedState(updated);
    saveDismissed(updated);
  };

  const visible = suggestions.filter((s) => {
    const ts = dismissed[s.code];
    return !ts || Date.now() - ts > DISMISS_TTL_MS;
  });

  if (isLoading || visible.length === 0) return null;

  const getParams = (s) => {
    const p = { ...s.params };
    if (s.code === 'peak_signup_day') {
      p.day_name = t(`marketing.dow_${DOW_KEYS[s.params.dow] ?? 'sunday'}`);
    }
    if (s.code === 'best_open_hour') {
      p.hour_label = formatHour(s.params.hour);
    }
    if (s.code?.startsWith('automation_off_')) {
      const key = TRIGGER_KEY[s.params.trigger_type] ?? s.params.key ?? '';
      p.automation = t(`marketing.auto_${key}_title`);
    }
    return p;
  };

  return (
    <div className="mb-4">
      <h3 className="mt-0 mb-2">{t('marketing.suggestions_title')}</h3>
      <div className="grid">
        {visible.map((s) => {
          const params = getParams(s);
          return (
            <div key={s.code} className="col-12 md:col-6 lg:col-4">
              <div className="surface-card border-round p-3 h-full flex flex-column gap-2">
                <div className="flex align-items-start justify-content-between gap-2">
                  <div className="flex align-items-center gap-2 mt-1">
                    <i
                      className={`${SEVERITY_ICON[s.severity] ?? 'pi pi-circle'} text-lg`}
                      style={{ color: `var(--${SEVERITY_TAG[s.severity] ?? 'info'}-500)` }}
                    />
                    <Tag severity={SEVERITY_TAG[s.severity]} value={t(`marketing.suggest_sev_${s.severity}`)} />
                  </div>
                  <Button
                    icon="pi pi-times"
                    text
                    rounded
                    severity="secondary"
                    size="small"
                    onClick={() => dismiss(s.code)}
                    aria-label={t('marketing.suggest_dismiss')}
                  />
                </div>
                <div className="font-semibold text-base">
                  {t(`marketing.suggest_${s.code}_title`, params)}
                </div>
                <div className="text-500 text-sm flex-grow-1">
                  {t(`marketing.suggest_${s.code}_body`, params)}
                </div>
                <SuggestionCta cta={s.cta} t={t} onViewChange={onViewChange} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
